if (!customElements.get('product-mobile-details')) {
  customElements.define('product-mobile-details', class extends HTMLElement {
    connectedCallback() {
      const product = this.closest('.thb-product-detail');
      const points = document.getElementById(this.dataset.pointsBlockId);
      if (!product || !points || !product.contains(points)) return;

      this.points = points;
      this.anchor = document.createComment('');
      points.before(this.anchor);
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.onLayoutChange = () => {
        if (this.mobileQuery.matches) {
          if (points.parentNode !== this) this.append(points);
        } else if (this.anchor.nextSibling !== points) {
          this.anchor.after(points);
        }
      };
      this.mobileQuery.addEventListener('change', this.onLayoutChange);
      this.onLayoutChange();
    }
    disconnectedCallback() {
      this.mobileQuery?.removeEventListener('change', this.onLayoutChange);
      this.anchor?.replaceWith(this.points);
      this.points = null;
      this.anchor = null;
    }
  });
}

if (!customElements.get('variant-selects')) {

  /**
   *  @class
   *  @function VariantSelects
   */
  class VariantSelects extends HTMLElement {
    constructor() {
      super();
      this.updateUrl = this.dataset.updateUrl === 'true';
      this.isDisabledFeature = this.dataset.isDisabled;
      this.addEventListener('change', this.onVariantChange);
      this.productWrapper = this.closest('.thb-product-detail');
      this.productSlider = this.productWrapper.querySelector('.product-images');
      this.hideVariants = this.dataset.hideVariants === 'true';
      this.preOrderVariantIds = (this.dataset.preOrderVariants || '').split(',').filter(Boolean);
      this.availableOptionsCache = new Map();
      this.productInfoCache = new Map();
      this.pendingSetDisabledFrame = null;
      this.pendingSetDisabledTimeout = null;
      this.fieldsets = null;
      this.fieldsetControls = null;
    }

    connectedCallback() {
      this.productWrapper = this.closest('.thb-product-detail');
      this.productSlider = this.productWrapper?.querySelector('.product-images');
      this.clearInitialRequiredOptions();
      this.updateOptions();
      this.updateMasterId();
      this.updateVariantText();

      if (this.currentVariant && this.preservedInitialRequiredOptionSelection) {
        this.updateVariantInput();
      }

      this.setDisabled();
      this.setImageSet();
      if (!this.dataset.sticky && this.dataset.renderedVariantId && !this.productInfoCache.size) {
        this.productInfoCache.set(this.dataset.renderedVariantId, { time: Date.now(), info: this.getProductInfo(document) });
      }
    }

    disconnectedCallback() {
      this.productInfoRequest?.abort();
      this.productInfoRequest = null;
      if (this.pendingSetDisabledFrame) cancelAnimationFrame(this.pendingSetDisabledFrame);
      if (this.pendingSetDisabledTimeout) clearTimeout(this.pendingSetDisabledTimeout);
      this.productInfoCache.clear();
    }

    getProductForms() {
      return Array.from(this.productWrapper.querySelectorAll('product-form')).filter((form) => form.dataset.section === this.dataset.section);
    }

    getSubmitButtons() {
      return this.getProductForms().flatMap((form) => Array.from(form.querySelectorAll('button[name="add"]')));
    }

    getOtherSelectors() {
      return Array.from(this.productWrapper.querySelectorAll('variant-selects')).filter((selector) => selector !== this && selector.dataset.section === this.dataset.section);
    }

    clearInitialRequiredOptions() {
      if (this.requiredOptionsInitialized) {
        return;
      }

      this.requiredOptionsInitialized = true;
      if (this.dataset.initialVariantId) {
        this.preservedInitialRequiredOptionSelection = true;
        return;
      }
      this.preservedInitialRequiredOptionSelection = false;
      this.getFieldsetControls().forEach(({ fieldset, select, inputs }) => {
        if (fieldset.dataset.requiresSelection !== 'true') {
          return;
        }

        if (select) {
          const selectedOption = select.options[select.selectedIndex];

          if (selectedOption && !selectedOption.defaultSelected) {
            this.preservedInitialRequiredOptionSelection = true;
            return;
          }

          Array.from(select.options).forEach((option) => {
            option.defaultSelected = option.value === '';
          });
          select.value = '';
          return;
        }

        const checkedInput = inputs.find((input) => input.checked);

        if (checkedInput && !checkedInput.defaultChecked) {
          this.preservedInitialRequiredOptionSelection = true;
          return;
        }

        inputs.forEach((input) => {
          input.checked = false;
          input.defaultChecked = false;
        });
      });
    }

    onVariantChange() {
      const primary = this.productWrapper.querySelector('variant-selects:not([data-sticky])');
      if (primary && primary !== this) {
        this.updateOptions();
        this.syncOptionsTo(primary);
        primary.onVariantChange();
        return;
      }
      this.productInfoRequest?.abort();
      this.productInfoRequest = null;
      this.infoFailed = false;
      this.updateOptions();
      this.updateMasterId();
      this.updateOther();
      if (Number(this.dataset.variantCount) > 250) {
        this.resolveVariant();
        return;
      }
      this.finishVariantChange();
    }

    finishVariantChange({ renderInfo = true } = {}) {
      const hasMissingRequiredOptions = this.hasMissingRequiredOptions();
      const isVariantUnavailable = (!this.currentVariant && !hasMissingRequiredOptions) || Boolean(this.currentVariant && !this.currentVariant.available);

      this.updatePickupAvailability();
      this.removeErrorMessage();
      this.updateVariantText();
      this.scheduleSetDisabled();

      if (!this.currentVariant) {
        this.setInfoLoading(false);
        if (hasMissingRequiredOptions) {
          this.updateMedia(this.getPartialVariant());
          this.toggleAddButton(false);
        } else {
          this.toggleAddButton(true);
          this.setUnavailable();
        }
      } else {
        this.toggleAddButton(!this.currentVariant.available, window.theme.variantStrings.soldOut);
        this.updateMedia();
        if (this.updateUrl) {
          this.updateURL();
        }
        this.updateVariantInput();
        if (renderInfo) this.renderProductInfo();
      }

      dispatchCustomEvent('product:variant-change', {
        variant: this.currentVariant,
        sectionId: this.dataset.section,
        variantUnavailable: isVariantUnavailable
      });
    }

    updateOptions() {
      this.options = [];
      this.getFieldsetControls().forEach(({ select, inputs }) => {
        if (select) {
          this.options.push(select.value);
        } else if (inputs.length) {
          const checkedInput = inputs.find((input) => input.checked);

          this.options.push(checkedInput ? checkedInput.value : '');
        }
      });
    }
    updateVariantText() {
      this.getFieldsetControls().forEach(({ fieldset }, i) => {
        let label = fieldset.querySelector('.form__label__value');
        if (label) {
          label.textContent = this.options[i] || '';
        }
      });
    }
    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
    }

    hasMissingRequiredOptions() {
      return this.getFieldsetControls().some(({ fieldset, select, inputs }) => {
        if (fieldset.dataset.requiresSelection !== 'true') {
          return false;
        }

        if (select) {
          return select.value === '';
        }

        return !inputs.some((input) => input.checked);
      });
    }

    getPartialVariant() {
      const selectedOptions = this.options
        .map((value, index) => {
          return { value, index };
        })
        .filter(({ value }) => value !== '');

      if (!selectedOptions.length) {
        return null;
      }

      if (this.resolvedPartialVariant && selectedOptions.every(({ value, index }) => this.resolvedPartialVariant.options[index] === value)) return this.resolvedPartialVariant;
      const matchingVariants = this.getVariantData().filter((variant) => {
        return selectedOptions.every(({ value, index }) => {
          return variant.options[index] === value;
        });
      });

      return matchingVariants.find((variant) => variant.featured_media) || matchingVariants[0] || null;
    }

    isPreOrderVariant(variant) {
      if (!variant) {
        return false;
      }

      if (this.preOrderVariantIds.includes(String(variant.id))) {
        return true;
      }

      if (!variant.inventory_management || variant.inventory_policy !== 'continue') {
        return false;
      }

      const inventoryQuantity = Number(variant.inventory_quantity);

      return Number.isFinite(inventoryQuantity) && inventoryQuantity <= 0;
    }

    getFieldsetControls() {
      this.fieldsets = this.fieldsets || Array.from(this.querySelectorAll('fieldset'));

      if (!this.fieldsetControls || this.fieldsetControls.length !== this.fieldsets.length) {
        this.fieldsetControls = this.fieldsets.map((fieldset) => {
          const select = fieldset.querySelector('select');

          return {
            fieldset,
            select,
            options: select ? Array.from(select.options) : [],
            inputs: select ? [] : Array.from(fieldset.querySelectorAll('input'))
          };
        });
      }

      return this.fieldsetControls;
    }

    scheduleSetDisabled() {
      if (this.isDisabledFeature != 'true') {
        return;
      }

      if (this.pendingSetDisabledFrame) {
        window.cancelAnimationFrame(this.pendingSetDisabledFrame);
      }
      if (this.pendingSetDisabledTimeout) {
        window.clearTimeout(this.pendingSetDisabledTimeout);
      }

      this.pendingSetDisabledFrame = window.requestAnimationFrame(() => {
        this.pendingSetDisabledFrame = null;
        this.pendingSetDisabledTimeout = window.setTimeout(() => {
          this.pendingSetDisabledTimeout = null;
          this.setDisabled();
        }, 0);
      });
    }

    syncOptionsTo(selector) {
      selector.getFieldsetControls().forEach(({ select, inputs }, index) => {
        const value = this.options[index] || '';
        if (select) select.value = value;
        else inputs.forEach((input) => { input.checked = input.value === value; });
      });
      selector.updateOptions();
      selector.updateMasterId();
      selector.updateVariantText();
      selector.scheduleSetDisabled();
      selector.querySelectorAll('product-color-swatches').forEach((swatches) => swatches.revealSelected?.());
    }

    updateOther() {
      this.getOtherSelectors().forEach((selector) => this.syncOptionsTo(selector));
    }

    updateMedia(variant = this.currentVariant) {
      if (!variant) return;
      if (!variant.featured_media || !this.productSlider) return;
      if (!customElements.get('product-slider')) {
        customElements.whenDefined('product-slider').then(() => {
          if (this.isConnected) this.updateMedia(this.currentVariant || this.getPartialVariant());
        });
        return;
      }

      let thumbnails = this.productWrapper.querySelector('.product-thumbnail-container');

      this.setActiveMedia(`#Slide-${this.dataset.section}-${variant.featured_media.id}`, `#Thumb-${this.dataset.section}-${variant.featured_media.id}`, this.productSlider, thumbnails, variant);
    }
    setActiveMedia(mediaId, thumbId, productSlider, thumbnails, variant = this.currentVariant) {
      let flkty = typeof Flickity === 'undefined' ? null : Flickity.data(productSlider),
        activeMedia = productSlider.querySelector(mediaId);

      if (!activeMedia) return;

      if (flkty && this.hideVariants) {
        // Size-only changes often keep the same media set. Avoid rebuilding Flickity in that case.
        const getActiveMediaIds = () => Array.from(productSlider.querySelectorAll('.product-images__slide.is-active')).map((media) => media.id).join('|');
        const previousActiveMediaIds = getActiveMediaIds();
        const activeMediaWasSelected = flkty.selectedElement === activeMedia;

        if (productSlider.querySelector('.product-images__slide.is-initial-selected')) {
          productSlider.querySelector('.product-images__slide.is-initial-selected').classList.remove('is-initial-selected');
        }
        [].forEach.call(productSlider.querySelectorAll('.product-images__slide-item--variant'), function (el) {
          el.classList.remove('is-active');
        });
        if (thumbnails) {
          if (thumbnails.querySelector('.product-thumbnail.is-initial-selected')) {
            thumbnails.querySelector('.product-thumbnail.is-initial-selected').classList.remove('is-initial-selected');
          }
          [].forEach.call(thumbnails.querySelectorAll('.product-images__slide-item--variant'), function (el) {
            el.classList.remove('is-active');
          });
        }

        activeMedia.classList.add('is-active');
        activeMedia.classList.add('is-initial-selected');

        this.setImageSetMedia(variant);

        let activeThumb = null;

        if (thumbnails) {
          activeThumb = thumbnails.querySelector(thumbId);

          if (activeThumb) {
            activeThumb.classList.add('is-active');
            activeThumb.classList.add('is-initial-selected');
          }
        }

        const activeMediaSetChanged = previousActiveMediaIds !== getActiveMediaIds();

        if (activeMediaSetChanged) {
          // The initial-selected class lets the new instance land on the target without a second animated select.
          productSlider.reInit({ scheduleResize: false });
          productSlider.setActiveThumbnail(activeThumb);
        } else if (!activeMediaWasSelected) {
          productSlider.selectCell(mediaId, true);
        }

      } else if (flkty) {
        productSlider.selectCell(mediaId, true);
      } else if (productSlider.dataset.layout === 'grid') {
        const activeMediaWasSelected = thumbnails && thumbnails.querySelector(thumbId)?.classList.contains('is-initial-selected');
        productSlider.querySelectorAll('.product-images__slide.is-initial-selected').forEach((media) => {
          media.classList.remove('is-initial-selected');
        });

        if (this.hideVariants) {
          productSlider.querySelectorAll('.product-images__slide-item--variant').forEach((media) => {
            media.classList.remove('is-active');
          });
          if (thumbnails) {
            thumbnails.querySelectorAll('.product-images__slide-item--variant').forEach((thumbnail) => {
              thumbnail.classList.remove('is-active');
            });
          }
        }

        activeMedia.classList.add('is-active', 'is-initial-selected');
        this.setImageSetMedia(variant);
        const expandedToRevealMedia = productSlider.updateGridMedia(activeMedia);
        productSlider.syncThumbnail(mediaId);
        if (!activeMediaWasSelected || expandedToRevealMedia) {
          activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

    }

    updateURL() {
      if (!this.currentVariant || !this.updateUrl) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState(window.history.state, '', url);
    }

    updateVariantInput() {
      const productForms = this.productWrapper.querySelectorAll('form[data-type="add-to-cart-form"], form.installment');
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', {
          bubbles: true
        }));
      });
    }

    updatePickupAvailability() {
      const pickUpAvailability = this.productWrapper.querySelector('.pickup-availability-wrapper');

      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.fetchAvailability(this.currentVariant.id);
      } else {
        pickUpAvailability.clearAvailability();
      }
    }

    removeErrorMessage() {
      const section = this.closest('section');
      if (!section) return;

      const forms = this.getProductForms();
      forms.forEach((form) => form.handleErrorMessage());
      forms[0]?.clearRequiredOptionErrors?.();
    }

    getSectionsToRender() {
      return [`badges-${this.dataset.section}`, `price-${this.dataset.section}`, `price-${this.dataset.section}--mobile`, `price-${this.dataset.section}--sticky`, `product-image-${this.dataset.section}--sticky`, `inventory-${this.dataset.section}`, `sku-${this.dataset.section}`, `sku-${this.dataset.section}--mobile`, `quantity-${this.dataset.section}`];
    }

    setInfoLoading(loading) {
      this.getSectionsToRender().filter((id) => id.startsWith('price-')).forEach((id) => {
        const price = document.getElementById(id);
        if (price && this.productWrapper.contains(price)) {
          price.classList.toggle('visibility-hidden', loading || this.infoFailed);
          price.setAttribute('aria-busy', String(loading));
        }
      });
    }

    getProductInfo(html) {
      const fragments = new Map();
      this.getSectionsToRender().forEach((id) => {
        const source = html.getElementById(id);
        if (source) fragments.set(id, source.innerHTML);
      });
      const quantity = html.getElementById('Quantity-' + this.dataset.section);
      const picker = Array.from(html.querySelectorAll('variant-selects')).find((picker) => picker.dataset.section === this.dataset.section && !picker.dataset.sticky);
      const variant = JSON.parse(picker?.querySelector('[data-selected-variant]')?.textContent || 'null');
      const availability = picker ? Array.from(picker.querySelectorAll('fieldset')).map((fieldset) => Array.from(fieldset.querySelectorAll('option[value]:not([value=""]), input[type="radio"]')).map((input) => ({ value: input.value, unavailable: input.disabled || input.classList.contains('is-disabled') }))) : [];
      const preOrder = Boolean(html.getElementById('product-form-' + this.dataset.section)?.querySelector('[data-pre-order-note]:not([hidden])'));
      return { fragments, variant, preOrder, availability, quantity: quantity ? Object.fromEntries(['min', 'max', 'step', 'data-min', 'data-max'].map((name) => [name, quantity.getAttribute(name)])) : null };
    }

    applyProductInfo(info) {
      if (info.preOrder && info.variant && !this.preOrderVariantIds.includes(String(info.variant.id))) this.preOrderVariantIds.push(String(info.variant.id));
      info.fragments.forEach((markup, id) => {
        if (id.startsWith('quantity-')) return;
        const destination = document.getElementById(id);
        if (!destination || !this.productWrapper.contains(destination)) return;
        if (destination.innerHTML !== markup) destination.innerHTML = markup;
        destination.classList.remove('visibility-hidden');
      });
      const quantity = this.productWrapper.querySelector('[name="quantity"]');
      if (quantity && info.quantity) {
        Object.entries(info.quantity).forEach(([name, value]) => {
          if (value === null) quantity.removeAttribute(name);
          else quantity.setAttribute(name, value);
        });
        const min = Number(quantity.min) || 1;
        const step = Number(quantity.step) || 1;
        const max = quantity.max ? Number(quantity.max) : Infinity;
        const value = min + Math.ceil((Math.max(min, Number(quantity.value) || min) - min) / step) * step;
        quantity.value = Math.min(value, Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : value);
        quantity.closest('quantity-selector')?.validateQtyRules();
      }
      this.setInfoLoading(false);
    }

    async renderProductInfo() {
      const variantId = this.currentVariant.id;
      const key = String(variantId);
      const cached = this.productInfoCache.get(key);
      if (cached && Date.now() - cached.time < 30000) {
        this.applyProductInfo(cached.info);
        return;
      }
      const controller = new AbortController();
      this.productInfoRequest = controller;
      this.setInfoLoading(true);
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', this.dataset.section);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Unable to update product information: ' + response.status);
        const markup = await response.text();
        if (this.productInfoRequest !== controller || !this.isConnected || this.currentVariant?.id !== variantId) return;
        const info = this.getProductInfo(new DOMParser().parseFromString(markup, 'text/html'));
        if (!info.fragments.has('price-' + this.dataset.section) && !info.fragments.has('price-' + this.dataset.section + '--mobile')) {
          if (this.productWrapper.querySelector('[id^="price-"]')) throw new Error('Product price is missing');
        }
        this.productInfoCache.set(key, { time: Date.now(), info });
        if (this.productInfoCache.size > 6) this.productInfoCache.delete(this.productInfoCache.keys().next().value);
        this.applyProductInfo(info);
      } catch (error) {
        if (error.name === 'AbortError' || this.productInfoRequest !== controller || !this.isConnected) return;
        this.showProductInfoError();
      } finally {
        if (this.productInfoRequest === controller) this.productInfoRequest = null;
      }
    }

    showProductInfoError() {
      this.resolvingVariant = false;
      this.infoFailed = true;
      this.setInfoLoading(false);
      this.getProductForms().forEach((form) => form.classList.add('product-form--variant-unavailable'));
      this.toggleAddButton(true);
      this.getProductForms().forEach((form) => form.handleErrorMessage(window.theme.variantStrings.updateError));
      const form = this.getProductForms().find((form) => !form.dataset.sticky);
      const wrapper = form?.querySelector('.product-form__error-message-wrapper');
      if (wrapper) {
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.dataset.productInfoRetry = '';
        retry.textContent = window.theme.variantStrings.retry;
        retry.addEventListener('click', () => this.onVariantChange(), { once: true });
        wrapper.append(retry);
      }
    }

    async resolveVariant() {
      const values = this.getFieldsetControls().map(({ select, inputs }) => select?.selectedOptions[0] || inputs.find((input) => input.checked));
      const ids = values.map((input) => input?.value ? input.dataset.optionValueId : '').filter(Boolean);
      const key = 'options:' + ids.join(',');
      const controller = new AbortController();
      this.productInfoRequest = controller;
      this.resolvingVariant = true;
      this.currentVariant = undefined;
      this.removeErrorMessage();
      this.updateVariantText();
      this.toggleAddButton(true);
      this.setInfoLoading(true);
      dispatchCustomEvent('product:variant-change', { variant: undefined, sectionId: this.dataset.section, variantUnavailable: true });
      try {
        const cached = this.productInfoCache.get(key);
        let info = cached && Date.now() - cached.time < 30000 ? cached.info : null;
        if (!info) {
          const url = new URL(this.dataset.url, window.location.origin);
          url.searchParams.set('section_id', this.dataset.section);
          if (ids.length) url.searchParams.set('option_values', ids.join(','));
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Unable to resolve product variant');
          const markup = await response.text();
          if (!this.isConnected || this.productInfoRequest !== controller) return;
          info = this.getProductInfo(new DOMParser().parseFromString(markup, 'text/html'));
          this.productInfoCache.set(key, { time: Date.now(), info });
          if (this.productInfoCache.size > 6) this.productInfoCache.delete(this.productInfoCache.keys().next().value);
        }
        if (!this.isConnected || this.productInfoRequest !== controller) return;
        this.resolvingVariant = false;
        this.resolvedPartialVariant = info.variant;
        this.currentVariant = this.hasMissingRequiredOptions() ? undefined : info.variant;
        [this, ...this.getOtherSelectors()].forEach((picker) => {
          picker.currentVariant = this.currentVariant;
          picker.getFieldsetControls().forEach(({ select, options, inputs }, index) => {
            (select ? options : inputs).forEach((input) => {
              const state = info.availability[index]?.find((item) => item.value === input.value);
              if (!state) return;
              if (select) input.disabled = state.unavailable;
              else input.classList.toggle('is-disabled', state.unavailable);
            });
          });
        });
        this.applyProductInfo(info);
        if (!this.currentVariant) this.updateMedia(info.variant);
        this.finishVariantChange({ renderInfo: false });
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected && this.productInfoRequest === controller) this.showProductInfoError();
      } finally {
        if (this.productInfoRequest === controller) this.productInfoRequest = null;
      }
    }

    toggleAddButton(disable = true, text = false) {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;

      const productTemplate = productForm.closest('product-form')?.getAttribute('template');
      const preOrderNote = productForm.querySelector('[data-pre-order-note]');
      const isPreOrder = !disable && (productTemplate?.includes('pre-order') || this.isPreOrderVariant(this.currentVariant));
      const dynamicCheckout = productForm.querySelector('[data-dynamic-checkout]');
      const submitButtons = this.getSubmitButtons();

      if (preOrderNote) preOrderNote.hidden = !isPreOrder;
      if (dynamicCheckout) dynamicCheckout.hidden = isPreOrder;

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');

        if (!submitButtonText) return;

        submitButton.classList.toggle('sold-out', disable);
        if (disable) {
          submitButton.setAttribute('disabled', 'disabled');
          if (text) submitButtonText.textContent = text;
        } else {
          if (!productForm.closest('product-form')?.submitting) submitButton.removeAttribute('disabled');

          if (isPreOrder) {
            submitButtonText.textContent = window.theme.variantStrings.preOrder;
          } else {
            submitButtonText.textContent = window.theme.variantStrings.addToCart;
          }

        }
      });

    }

    setUnavailable() {
      const submitButtons = this.getSubmitButtons();
      const price = document.getElementById(`price-${this.dataset.section}`);
      const mobilePrice = document.getElementById(`price-${this.dataset.section}--mobile`);
      const badges = document.getElementById(`badges-${this.dataset.section}`);
      const price_fixed = document.getElementById(`price-${this.dataset.section}--sticky`);

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector('.single-add-to-cart-button--text');
        if (!submitButtonText) return;
        submitButtonText.textContent = window.theme.variantStrings.unavailable;
        submitButton.classList.add('sold-out');
      });
      if (price) price.classList.add('visibility-hidden');
      if (mobilePrice) mobilePrice.classList.add('visibility-hidden');
      if (badges) badges.classList.add('visibility-hidden');
      if (price_fixed) price_fixed.classList.add('visibility-hidden');
    }

    setDisabled() {
      if (Number(this.dataset.variantCount) > 250) return;
      if (this.isDisabledFeature != 'true') {
        return;
      }
      const variant_data = this.getVariantData();


      if (variant_data) {

        let selected_options = false;
        if (this.currentVariant) {
          selected_options = this.currentVariant.options.map((value, index) => {
            return {
              value,
              index: `option${index + 1}`
            };
          });
        } else {
          let found_option = variant_data.find(option => {
            return option.option1 === this.options[0];
          });
          if (found_option) {
            selected_options = [
              {
                "value": this.options[0],
                "index": "option1"
              },
              {
                "value": found_option.option2,
                "index": "option2"
              }
            ];
          } else {
            return;
          }
        }
        const available_options = this.getAvailableOptions(variant_data, selected_options);

        this.getFieldsetControls().forEach(({ select, options, inputs }, i) => {
          const fieldset_options = Object.values(available_options)[i];

          if (fieldset_options) {
            if (select) {
              fieldset_options.forEach((option, option_i) => {
                const optionElement = options.find((item) => item.value === option.value);

                if (optionElement) {
                  optionElement.disabled = option.isUnavailable;
                }
              });
            } else if (inputs.length) {
              inputs.forEach((input, input_i) => {
                const fieldsetOption = fieldset_options.find((option) => option.value === input.value);

                if (fieldsetOption) {
                  input.classList.toggle('is-disabled', fieldsetOption.isUnavailable);
                }
              });
            }
          }
        });

      }
      return true;
    }

    getAvailableOptions(variants, selectedOptions) {
      const primary = this.productWrapper.querySelector('variant-selects:not([data-sticky])');
      if (primary && primary !== this) return primary.getAvailableOptions(variants, selectedOptions);
      const cacheKey = selectedOptions.map(({ value, index }) => `${index}:${value}`).join('|');

      if (this.availableOptionsCache.has(cacheKey)) {
        return this.availableOptionsCache.get(cacheKey);
      }

      const availableOptions = this.createAvailableOptionsTree(variants, selectedOptions);
      this.availableOptionsCache.set(cacheKey, availableOptions);
      if (this.availableOptionsCache.size > 64) this.availableOptionsCache.delete(this.availableOptionsCache.keys().next().value);

      return availableOptions;
    }

    getImageSetName(variant_name) {
      return String(variant_name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
    }

    setImageSet() {
      if (!this.productSlider) return;

      let dataSetEl = this.productSlider.querySelector('[data-set-name]');
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        const fieldset = this.getFieldsetControls().find(({ fieldset }) => fieldset.dataset.handle === this.imageSetName)?.fieldset;
        if (!fieldset) return;
        this.imageSetIndex = fieldset.dataset.index;
        this.dataset.imageSetIndex = this.imageSetIndex;
        this.setImageSetMedia(this.currentVariant || this.getPartialVariant());
      }
    }
    setImageSetMedia(variant = this.currentVariant) {
      if (!this.imageSetIndex || !variant) {
        return;
      }

      const control = this.getFieldsetControls().find(({ fieldset }) => fieldset.dataset.index === this.imageSetIndex);
      const choices = control?.select ? control.options : control?.inputs;
      const choice = choices?.find((item) => item.value === variant[this.imageSetIndex]);
      let setValue = choice?.dataset.valueHandle || this.getImageSetName(variant[this.imageSetIndex]);
      let group = this.imageSetName + '_' + setValue;
      let selected_set_images = this.productWrapper.querySelectorAll(`.product-images__slide[data-set-name="${this.imageSetName}"]`),
        selected_set_thumbs = this.productWrapper.querySelectorAll(`.product-thumbnail[data-set-name="${this.imageSetName}"]`);

      if (this.hideVariants) {
        // Product images
        this.productWrapper.querySelectorAll('.product-images__slide').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_images.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });

        // Product thumbnails
        this.productWrapper.querySelectorAll('.product-thumbnail').forEach(thumb => {
          if (thumb.dataset.group && thumb.dataset.group !== group) {
            thumb.classList.remove('is-active');
          }
        });
        selected_set_thumbs.forEach(thumb => {
          thumb.classList.toggle('is-active', thumb.dataset.group === group);
        });
      }

    }

    createAvailableOptionsTree(variants, currentlySelectedValues) {
      // Reduce variant array into option availability tree
      const currentOption1 = currentlySelectedValues.find(({ index }) => index === 'option1');
      const currentOption2 = currentlySelectedValues.find(({ index }) => index === 'option2');
      const optionIndexes = ['option1', 'option2', 'option3'];

      return variants.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        optionIndexes.forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = { value: variant[index], isUnavailable: true };
            options[index].push(entry);
          }

          switch (index) {
            case 'option1':
              // Option1 inputs should always remain enabled based on all available variants
              entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              break;
            case 'option2':
              // Option2 inputs should remain enabled based on available variants that match first option group
              if (currentOption1 && variant.option1 === currentOption1.value) {
                entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              }
              break;
            case 'option3':
              // Option 3 inputs should remain enabled based on available variants that match first and second option group
              if (currentOption1 && variant.option1 === currentOption1.value && currentOption2 && variant.option2 === currentOption2.value) {
                entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
              }
          }
        });

        return options;
      }, { option1: [], option2: [], option3: [] });
    }

    getVariantData() {
      const primary = this.productWrapper.querySelector('variant-selects:not([data-sticky])');
      if (primary && primary !== this) return primary.getVariantData();
      this.variantData ||= JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]').filter(Boolean);
      return this.variantData;
    }
  }
  customElements.define('variant-selects', VariantSelects);

}

/**
 *  @class
 *  @function ProductForm
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.sticky = this.dataset.sticky;
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      if (!this.form) return;
      this.form.querySelector('[name=id]').disabled = false;
      if (!this.sticky) {
        this.boundOnSubmit ||= this.onSubmitHandler.bind(this);
        this.form.addEventListener('submit', this.boundOnSubmit);
      }
      this.cartNotification = document.querySelector('cart-notification');
      this.body = document.body;

      this.hideErrors = this.dataset.hideErrors === 'true';
      this.onRequiredOptionOverlayClick = (event) => {
        const overlay = event.target.closest('[data-required-option-overlay]');

        if (!overlay) {
          return;
        }

        event.preventDefault();
        this.validateRequiredOptions(overlay);
      };
      this.onRequiredOptionStateChange = (event) => {
        if (event.detail?.sectionId === this.dataset.section) {
          this.updateRequiredOptionState();
          this.updateVariantPurchaseState(event.detail?.variantUnavailable);
        }
      };
      this.addEventListener('click', this.onRequiredOptionOverlayClick);
      if (!this.sticky) document.addEventListener('product:variant-change', this.onRequiredOptionStateChange);
      this.updateRequiredOptionState();
    }
    disconnectedCallback() {
      if (this.boundOnSubmit) this.form?.removeEventListener('submit', this.boundOnSubmit);
      this.removeEventListener('click', this.onRequiredOptionOverlayClick);
      document.removeEventListener('product:variant-change', this.onRequiredOptionStateChange);
    }
    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitting || !this.isConnected) return;

      if (!this.form.reportValidity()) {
        return;
      }

      if (!this.validateRequiredOptions(evt.submitter)) {
        return;
      }

      const selector = this.closest('.thb-product-detail')?.querySelector('variant-selects:not([data-sticky])');
      if (this.classList.contains('product-form--variant-unavailable') || selector?.infoFailed || selector?.resolvingVariant) return;
      const submitButtons = this.getSectionProductForms().flatMap((form) => Array.from(form.querySelectorAll('button[name="add"]')));
      this.submitting = true;

      submitButtons.forEach((submitButton) => {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
      });

      this.handleSectionErrorMessage();


      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };


      let formData = new FormData(this.form);

      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            dispatchCustomEvent('product:variant-error', {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message
            });
            if (response.status === 422) {
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                bubbles: true
              }));
            }
            this.handleErrorMessage(response.description);
            return;
          }

          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          if (this.isConnected) this.handleSectionErrorMessage(window.theme.variantStrings.addError);
          console.error(e);
        })
        .finally(() => {
          this.submitting = false;
          submitButtons.forEach((submitButton) => {
            const selector = this.closest('.thb-product-detail')?.querySelector('variant-selects:not([data-sticky])');
            submitButton.disabled = selector ? Boolean(selector.infoFailed || selector.resolvingVariant || (!selector.currentVariant && !selector.hasMissingRequiredOptions()) || (selector.currentVariant && !selector.currentVariant.available)) : this.classList.contains('product-form--variant-unavailable');
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
        });
    }

    getSectionProductForms() {
      return Array.from((this.closest('.thb-product-detail') || this).querySelectorAll('product-form')).filter((productForm) => {
        return productForm.dataset.section === this.dataset.section;
      });
    }
    handleSectionErrorMessage(errorMessage = false) {
      this.getSectionProductForms().forEach((productForm) => {
        productForm.handleErrorMessage?.(errorMessage);
      });
    }
    getRequiredOptionGroups() {
      const groups = new Map();
      const variantSelectors = Array.from((this.closest('.thb-product-detail') || this).querySelectorAll('variant-selects')).filter((selector) => {
        return selector.dataset.section === this.dataset.section;
      });

      variantSelectors.forEach((selector) => {
        selector.querySelectorAll('[data-requires-selection="true"]').forEach((fieldset) => {
          const key = fieldset.dataset.index || fieldset.dataset.handle;

          if (!groups.has(key)) {
            groups.set(key, []);
          }

          groups.get(key).push(fieldset);
        });
      });

      return Array.from(groups.values());
    }
    hasRequiredOptionValue(fieldset) {
      const select = fieldset.querySelector('select');

      if (select) {
        return select.value !== '';
      }

      return Array.from(fieldset.querySelectorAll('input')).some((input) => input.checked);
    }
    clearRequiredOptionErrors() {
      this.getRequiredOptionGroups().flat().forEach((fieldset) => {
        fieldset.classList.remove('is-missing-required');
        fieldset.removeAttribute('aria-invalid');
      });
    }
    getMissingRequiredOptionGroup() {
      return this.getRequiredOptionGroups().find((fieldsets) => {
        return !fieldsets.some((fieldset) => this.hasRequiredOptionValue(fieldset));
      });
    }
    updateRequiredOptionState() {
      const hasMissingRequiredOption = Boolean(this.getMissingRequiredOptionGroup());

      this.getSectionProductForms().forEach((productForm) => {
        productForm.classList.toggle('product-form--missing-required-option', hasMissingRequiredOption);
      });

      return hasMissingRequiredOption;
    }
    updateVariantPurchaseState(isVariantUnavailable = false) {
      this.getSectionProductForms().forEach((productForm) => {
        productForm.classList.toggle('product-form--variant-unavailable', Boolean(isVariantUnavailable));
      });
    }
    validateRequiredOptions(submitter = null) {
      const missingOptionGroup = this.getMissingRequiredOptionGroup();

      this.clearRequiredOptionErrors();
      this.updateRequiredOptionState();

      if (!missingOptionGroup) {
        return true;
      }

      const message = this.dataset.requiredOptionMessage || window.theme?.variantStrings?.selectSize || 'Please select a size.';
      const submitterSticky = submitter?.closest('product-add-to-cart-sticky');
      const preferredFieldset = submitterSticky ? missingOptionGroup.find((fieldset) => submitterSticky.contains(fieldset)) : null;
      const visibleFieldset = preferredFieldset || missingOptionGroup.find((fieldset) => fieldset.offsetParent !== null) || missingOptionGroup[0];

      missingOptionGroup.forEach((fieldset) => {
        fieldset.classList.add('is-missing-required');
        fieldset.setAttribute('aria-invalid', 'true');
      });

      this.handleSectionErrorMessage(message);

      if (visibleFieldset) {
        const focusTarget = visibleFieldset.querySelector('select, input:not(.is-disabled)');

        visibleFieldset.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        if (focusTarget) {
          focusTarget.focus({
            preventScroll: true
          });
        }
      }

      return false;
    }
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      let refreshCart = false;
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        const html = parsedState.sections?.[section.section];
        if (!html) {
          refreshCart = true;
          return;
        }
        elementToReplace.innerHTML = this.getSectionInnerHTML(html, section.selector);

        if (section.id === 'Cart-Drawer') {
          document.getElementById('Cart-Drawer')?.removeProductEvent();
        }
      }));

      if (refreshCart) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      window.cartPage?.removeProductEvent();
      window.cartPage?.termsCheckbox();
      let product_drawer = document.getElementById('Product-Drawer'),
        search_drawer = document.getElementById('Search-Drawer');

      if (search_drawer?.classList.contains('active')) {
        search_drawer.classList.remove('active');
      }
      if (product_drawer && product_drawer.contains(this)) {
        product_drawer.classList.remove('active');
        this.body.classList.remove('open-cc--product');
        if (document.getElementById('Cart-Drawer')) {
          const cartDrawer = document.getElementById('Cart-Drawer');
          if (typeof cartDrawer.openDrawer === 'function') {
            cartDrawer.openDrawer();
          } else if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        }
      } else if (document.getElementById('Cart-Drawer')) {
        const cartDrawer = document.getElementById('Cart-Drawer');
        if (typeof cartDrawer.openDrawer === 'function') {
          cartDrawer.openDrawer();
        } else if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      this.querySelector('[data-product-info-retry]')?.remove();
      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}


/**
 *  @class
 *  @function ProductAddToCartSticky
 */
/**
 *  @class
 *  @function ProductSidePanelLinks
 */
if (!customElements.get('side-panel-links')) {
  class ProductSidePanelLinks extends HTMLElement {
    constructor() {
      super();
      this.links = this.querySelectorAll('button');
      this.drawer = document.getElementById('Product-Information-Drawer');
      this.buttons = this.drawer.querySelector('.side-panel-content--tabs');
      this.panels = this.drawer.querySelector('.side-panel-content--inner').querySelectorAll('.side-panel-content--tab-panel');
      this.body = document.body;
    }
    connectedCallback() {
      this.setupObservers();
    }
    disconnectedCallback() {

    }
    setupObservers() {
      this.links.forEach((item, i) => {
        item.addEventListener('click', (e) => {
          this.body.classList.add('open-cc');
          this.buttons.toggleActiveClass(i);
          this.drawer.classList.add('active');
        });
      });
    }
  }

  customElements.define('side-panel-links', ProductSidePanelLinks);
}

if (typeof addIdToRecentlyViewed !== "undefined") {
  addIdToRecentlyViewed();
}

if (!window.loadProductComponents) {
  const componentLoads = new Map();
  window.loadProductComponents = (root = document) => {
    return Promise.all(Object.entries(window.theme.productAssets || {}).map(([tag, url]) => {
      if (customElements.get(tag) || (!root.matches?.(tag) && !root.querySelector(tag))) return;
      if (!componentLoads.has(tag)) {
        const promise = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = url;
          script.onload = resolve;
          script.onerror = () => {
            componentLoads.delete(tag);
            script.remove();
            reject(new Error('Unable to load ' + tag));
          };
          document.head.appendChild(script);
        });
        componentLoads.set(tag, promise);
      }
      return componentLoads.get(tag);
    }));
  };
  document.addEventListener('shopify:section:load', (event) => window.loadProductComponents(event.target).catch(console.error));
}
window.loadProductComponents().catch(console.error);
